/**
 * Signage Hub — workspace-wide calm display for digital signage screens.
 *
 * Route: /signage
 * Auth: pair_token via localStorage `signage_pair_token`.
 * Polls /api/signage/snapshot every 45s.
 */
import { useCallback, useEffect, useState } from "react";
import { Sparkles, CalendarDays } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const KIND_COLOR = {
  field_trip: "#8A9984",
  parent_meeting: "#A89CC8",
  holiday: "#E6BFA6",
  closure: "#D4A85F",
  celebration: "#FFB800",
  info: "#9BA995",
  activity: "#E0DED9",
  staff_meeting: "#1F3A2A",
  training: "#7E8C72",
};

export default function SignageHub() {
  const [token, setToken] = useState(
    () => localStorage.getItem("signage_pair_token") || ""
  );
  const [input, setInput] = useState("");
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/signage/snapshot`, {
        headers: { "X-Alouvya-Pair-Token": token },
      });
      if (r.status === 403) {
        setError("Pair token rejected — ask your admin for a fresh one.");
        return;
      }
      if (!r.ok) {
        setError(`Snapshot failed (${r.status})`);
        return;
      }
      setError(null);
      setSnap(await r.json());
    } catch {
      setError("Connection paused.");
    }
  }, [token]);

  useEffect(() => {
    fetchSnapshot();
    if (!token) return;
    const id = setInterval(fetchSnapshot, 45_000);
    return () => clearInterval(id);
  }, [token, fetchSnapshot]);

  if (!token) {
    return (
      <PairingScreen
        input={input}
        setInput={setInput}
        onConfirm={() => {
          if (!input.trim()) return;
          localStorage.setItem("signage_pair_token", input.trim());
          setToken(input.trim());
        }}
      />
    );
  }

  if (error) {
    return (
      <div
        data-testid="signage-error"
        className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center p-12"
      >
        <div className="text-center">
          <p className="font-serif text-3xl">{error}</p>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("signage_pair_token");
              setToken("");
            }}
            data-testid="signage-reset"
            className="mt-6 rounded-full bg-[#8A9984] text-[#0e1f17] px-6 py-3 text-xs tracking-[0.32em] uppercase"
          >
            Pair again
          </button>
        </div>
      </div>
    );
  }

  if (!snap) {
    return (
      <div className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center">
        <p className="font-serif italic">A quiet moment…</p>
      </div>
    );
  }

  return (
    <div
      data-testid="signage-hub"
      className="min-h-screen bg-[#0e1f17] text-[#E0DED9] px-16 py-12 flex flex-col"
    >
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
            {snap.workspace?.name || "Alouvya"}
          </p>
          <h1
            data-testid="signage-headline"
            className="mt-3 font-serif text-6xl tracking-tight"
          >
            {snap.present_count} {snap.present_count === 1 ? "child" : "children"} with us
          </h1>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
            Right now
          </p>
          <p
            data-testid="signage-clock"
            className="mt-3 font-serif text-5xl tabular-nums tracking-tight"
          >
            {fmtClock(snap.now)}
          </p>
        </div>
      </header>

      <section className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 flex-1">
        {snap.zones?.length === 0 ? (
          <p
            data-testid="signage-empty"
            className="font-serif italic text-[#8A9984] text-2xl col-span-full"
          >
            All quiet across the rooms.
          </p>
        ) : (
          snap.zones.map((z) => (
            <article
              key={z.zone_id}
              data-testid={`signage-zone-${z.zone_id}`}
              className="rounded-3xl bg-[#152e22] border border-[#8A9984]/10 p-6 flex flex-col gap-3"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
                {z.kind ? z.kind.replace("_", " ") : "—"}
              </p>
              <p className="font-serif text-2xl tracking-tight">{z.zone_name}</p>
              <p className="font-serif text-5xl tabular-nums tracking-tight mt-auto">
                {z.child_count}
              </p>
              {z.child_names?.length > 0 && (
                <p className="text-[11px] tracking-[0.18em] uppercase text-[#8A9984] truncate">
                  {z.child_names.slice(0, 3).join(" · ")}
                  {z.child_names.length > 3 && " · …"}
                </p>
              )}
            </article>
          ))
        )}
      </section>

      {snap.events_today?.length > 0 && (
        <footer className="mt-10 pt-6 border-t border-[#8A9984]/15">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984] flex items-center gap-2">
            <CalendarDays size={11} strokeWidth={1.6} />
            Today
          </p>
          <ul
            data-testid="signage-events"
            className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {snap.events_today.slice(0, 4).map((ev, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-2xl bg-[#152e22] px-4 py-3"
              >
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-full"
                  style={{ background: KIND_COLOR[ev.kind] || "#9BA995" }}
                />
                <span className="text-[#E0DED9] flex-1 truncate font-serif">
                  {ev.title}
                </span>
                {ev.location && (
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#8A9984] truncate">
                    {ev.location}
                  </span>
                )}
                <span className="text-xs tabular-nums text-[#8A9984]">
                  {fmtTime(ev.starts_at)}
                </span>
              </li>
            ))}
          </ul>
        </footer>
      )}
    </div>
  );
}

function PairingScreen({ input, setInput, onConfirm }) {
  return (
    <div
      data-testid="signage-pairing"
      className="min-h-screen bg-[#0e1f17] text-[#E0DED9] flex items-center justify-center p-8"
    >
      <div className="max-w-md w-full">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#8A9984]">
          Digital signage
        </p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">
          Welcome the lobby.
        </h1>
        <p className="mt-4 text-sm text-[#8A9984] leading-relaxed">
          Paste the pair token your admin gave this screen.
        </p>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="signage-pair-input"
          placeholder="pair token"
          className="mt-8 w-full bg-[#152e22] border border-[#8A9984]/15 rounded-2xl px-5 py-4 text-[#E0DED9] outline-none focus:border-[#8A9984]/40 font-mono text-xs"
        />
        <button
          type="button"
          onClick={onConfirm}
          data-testid="signage-pair-confirm"
          className="mt-5 w-full rounded-2xl bg-[#8A9984] text-[#0e1f17] py-3 text-xs tracking-[0.32em] uppercase font-medium hover:bg-[#9aaa92] transition-colors"
        >
          Settle this screen
        </button>
      </div>
    </div>
  );
}
